import './src/styles/global.css'
import i18next from 'i18next'
import { navigate } from 'gatsby'

import { supportedLanguages, defaultLanguage } from './src/constants/languages'
import {
    applyConsentFromCookieYes,
    loadGaOnce,
    trackPageView,
} from './src/lib/ga'

// FONTS
import '@fontsource-variable/winky-rough'
import '@fontsource-variable/winky-rough/wght-italic.css'

/* ------------------------------- Hilfsfunktionen ---------------------------------- */
const detectLanguage = (): string => {
    // 1) Sprache aus localStorage
    const stored =
        typeof window !== 'undefined'
            ? localStorage.getItem('gatsby-i18next-language')
            : null

    // 2) Browser-Sprache
    const browser =
        typeof navigator !== 'undefined'
            ? navigator.language?.split('-')[0]
            : null
    const candidate = stored || browser || defaultLanguage
    return supportedLanguages.includes(
        candidate as (typeof supportedLanguages)[number]
    )
        ? (candidate as (typeof supportedLanguages)[number])
        : defaultLanguage
}

const applyDirAttributes = (lang: string) => {
    const html = document.documentElement
    html.setAttribute('lang', lang)
    html.setAttribute('dir', i18next.dir(lang)) // „rtl“ für ar/he, sonst „ltr“
}

/** Erstes URL-Segment als Sprachpräfix */
const getLangFromPath = (pathname: string): string | null => {
    const [, maybeLang] = pathname.match(/^\/([^/]+)/) || []
    return supportedLanguages.includes(
        maybeLang as (typeof supportedLanguages)[number]
    )
        ? (maybeLang as (typeof supportedLanguages)[number])
        : null
}

/* ------------------------------ GA: sehr früh initialisieren ------------------------------ */
export const onClientEntry = async (): Promise<void> => {
    // 1) Consent Mode Status aus CookieYes in gtag spiegeln (default=denied wird in SSR + ga.ts gesetzt)
    applyConsentFromCookieYes()

    // 2) gtag.js IMMER laden (auch ohne Consent → cookieless pings möglich)
    await loadGaOnce()

    // 3) Auf spätere Consent-Änderungen reagieren – nur Consent updaten (keinen Extra-Pageview!)
    document.addEventListener('cookieyes_consent_update', () => {
        applyConsentFromCookieYes()
    })
}

/* --------------------------- Erste Client-Render-Phase ---------------------------- */
export const onInitialClientRender = (): void => {
    const { pathname, search, hash } = window.location
    const langInUrl = getLangFromPath(pathname)

    if (!langInUrl) {
        // kein Präfix → einmalig umleiten (der Pageview kommt dann über onRouteUpdate)
        const userLang = detectLanguage()
        const newPath =
            `/${userLang}` + (pathname === '/' ? '' : pathname) + search + hash
        navigate(newPath, { replace: true })
        return
    }

    // Präfix vorhanden → HTML-Attribute setzen
    applyDirAttributes(langInUrl)

    // Landing-Page JETZT tracken (auch ohne Consent → cookieless)
    trackPageView(window.location.href)
}

/* ----------------------- Bei nachfolgenden Routenwechseln ------------------------- */
export const onRouteUpdate = ({ location }: { location: Location }): void => {
    const langInUrl = getLangFromPath(location.pathname) || defaultLanguage
    applyDirAttributes(langInUrl)

    // Jede SPA-Navigation tracken (Consent entscheidet im Hintergrund über Cookies)
    trackPageView(window.location.href)
}
